/**
 * Sidebar variant for the "Files" contour.
 *
 * Same chrome as the boards Sidebar (workspace header, collapse button,
 * resize handle) but the body hosts a <FileExplorerPanel /> in place of
 * the boards list. Keeps the localStorage width contract intact.
 *
 * When the user clicks a file, we navigate to `/files/$/...` so the URL
 * reflects the selection and the main pane mounts the right viewer.
 */
import type { FsTreeNode } from '@chaos/types/fs';
import { useNavigate } from '@tanstack/react-router';
import { ChevronsLeft, ChevronsUpDown, LogOut, Moon, Settings, Sun } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { authApi } from '../../lib/api';
import { useResizablePanel } from '../../lib/use-resizable-panel';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { useFileExplorerStore } from '../../stores/file-explorer-store';
import { useThemeStore } from '../../stores/theme-store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { FileExplorerPanel } from './file-explorer-panel';

interface FilesSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function FilesSidebar({ collapsed, onToggle }: FilesSidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = useAuthStore((s) => s.user);
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const pickerResolve = useFileExplorerStore((s) => s.picker.resolve);
  const pickResult = useFileExplorerStore((s) => s.pickResult);

  const activeWorkspace = workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.workspace;

  const { width, isDragging, onHandleMouseDown } = useResizablePanel({
    storageKey: 'chaos-sidebar-width',
    defaultWidth: 280,
    minWidth: 220,
    maxWidth: 420,
    side: 'left',
  });

  const handleSignOut = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    setUnauthenticated();
    window.location.href = '/login';
  };

  const handleFileSelected = React.useCallback(
    (node: FsTreeNode) => {
      // If a picker is queued, hand the path back instead of navigating.
      if (pickerResolve) {
        pickResult(node.path);
        return;
      }
      void navigate({
        to: '/files/$',
        params: { _splat: encodePath(node.path) },
      });
    },
    [navigate, pickerResolve, pickResult],
  );

  if (collapsed) return null;

  return (
    <aside
      aria-label="File explorer navigation"
      style={{ width }}
      className={cn(
        'relative flex h-screen shrink-0 flex-col border-r border-border bg-muted/30',
        isDragging && 'select-none',
      )}
    >
      {/* Workspace header (mirrors the boards sidebar exactly so the
          chrome stays consistent when the contour switches). */}
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group flex flex-1 items-center justify-between px-3 py-2.5 text-left hover:bg-foreground/5"
              aria-label="Account menu"
            >
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-[13px] font-semibold tracking-tight text-foreground">
                  {activeWorkspace?.name ?? user?.displayName ?? user?.email ?? t('app.name')}
                </span>
                <span className="truncate text-[11px] text-muted-foreground">Files</span>
              </div>
              <ChevronsUpDown
                className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="bottom" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{user?.displayName ?? user?.email}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === 'dark' ? t('nav.light_mode') : t('nav.dark_mode')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate({ to: '/settings/profile' })}>
              <Settings className="h-4 w-4" />
              <span>{t('settings.profile.title')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
              <span>{t('dashboard.sign_out')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Collapse sidebar"
          className="mr-1.5 flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
          <ChevronsLeft className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {/* The actual file-explorer panel — owns the tree, search, and uploads. */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <FileExplorerPanel onFileSelected={handleFileSelected} />
      </div>

      {/* Resize handle. */}
      <button
        type="button"
        aria-label="Resize sidebar"
        tabIndex={-1}
        onMouseDown={onHandleMouseDown}
        className={cn(
          'absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize border-0 bg-transparent p-0 transition-colors',
          isDragging ? 'bg-foreground/20' : 'hover:bg-foreground/10',
        )}
      />
    </aside>
  );
}

/**
 * URL-encode an absolute path for the `/files/$` splat segment. Slashes
 * inside the path are preserved (the splat parameter handles them), but
 * other reserved characters (?, #, %) are escaped.
 */
export function encodePath(path: string): string {
  return path
    .split('/')
    .filter(Boolean)
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

export function decodePath(splat: string): string {
  return (
    '/' +
    splat
      .split('/')
      .map((seg) => decodeURIComponent(seg))
      .join('/')
  );
}
